import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  TablePagination,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  getStorageExpectedIncome,
  getStorageExpectedIncomeFirstCharges,
} from "../../services/api";
import "./style.css";

// Storage Expected Income Tab Component
const StorageExpectedIncomeTab = ({
  data,
  loading,
  error,
  searchTerm,
  setSearchTerm,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  onExportExcel,
}) => {
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    // Filter data based on search term
    if (searchTerm.trim() === "") {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          item.job_number?.toLowerCase().includes(searchLower) ||
          item.client_name?.toLowerCase().includes(searchLower) ||
          item.oost_date?.toLowerCase().includes(searchLower) ||
          item.monthly_charge?.toString().includes(searchLower)
        );
      });
      setFilteredData(filtered);
    }
    setPage(0); // Reset to first page when search changes
  }, [searchTerm, data, setPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate total expected income
  const totalIncome = filteredData.reduce((sum, item) => {
    return sum + (parseFloat(item.monthly_charge) || 0);
  }, 0);

  // Get paginated data
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <TextField
          placeholder="Search"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: "200px",
            backgroundColor: "#fff",
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            variant="h6"
            fontWeight={600}
            color="#2B3033"
            sx={{ minWidth: "fit-content" }}
          >
            Total: ${" "}
            {totalIncome.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
          <IconButton
            onClick={onExportExcel}
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
              width: 48,
              height: 48,
            }}
            title="Download Excel"
          >
            <CloudDownloadIcon />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0}>
            <Table
              sx={{
                "& .MuiTableCell-root": {
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#F4F5F8" }}>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    Job Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    Lot
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    Client Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    OOST Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    Monthly Charge
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    Office
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">
                        {searchTerm ? "No results found" : "No data available"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row, index) => (
                    <TableRow
                      key={`${row.job_number}-${index}`}
                      hover
                      sx={{
                        "&:hover": {
                          backgroundColor: "#F9F9F9",
                        },
                      }}
                    >
                      <TableCell>{row.job_number}</TableCell>
                      <TableCell>{row.lot}</TableCell>
                      <TableCell>{row.client_name}</TableCell>
                      <TableCell>{row.oost_date}</TableCell>
                      <TableCell>
                        {parseFloat(row.monthly_charge || 0).toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}
                      </TableCell>
                      <TableCell>{row.office}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        </>
      )}
    </>
  );
};

// Storage Expected First Charge Tab Component
const StorageExpectedFirstChargeTab = ({
  data,
  loading,
  error,
  searchTerm,
  setSearchTerm,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  monthType,
  setMonthType,
  onExportExcel,
}) => {
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    // Filter data based on search term
    if (searchTerm.trim() === "") {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          item.job_number?.toLowerCase().includes(searchLower) ||
          item.client_name?.toLowerCase().includes(searchLower) ||
          item.oost_date?.toLowerCase().includes(searchLower) ||
          item.first_charge?.toString().includes(searchLower) ||
          item.into_storage_date?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredData(filtered);
    }
    setPage(0); // Reset to first page when search changes
  }, [searchTerm, data, setPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString || dateString === "-") return "-";
    try {
      return moment(dateString).format("MM-DD-YYYY");
    } catch (e) {
      return dateString;
    }
  };

  // Calculate total first charge
  const totalFirstCharge = filteredData.reduce((sum, item) => {
    return sum + (parseFloat(item.first_charge) || 0);
  }, 0);

  // Get paginated data
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            placeholder="Search"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: "200px",
              backgroundColor: "#fff",
            }}
          />
          <FormControl
            size="small"
            sx={{ minWidth: 180, backgroundColor: "#fff" }}
          >
            <InputLabel id="month-type-label">Type of month</InputLabel>
            <Select
              labelId="month-type-label"
              id="month-type-select"
              value={monthType}
              label="Type of month"
              onChange={(e) => setMonthType(e.target.value)}
            >
              <MenuItem value="current">Current Month</MenuItem>
              <MenuItem value="next">Next Month</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            variant="h6"
            fontWeight={600}
            color="#2B3033"
            sx={{ minWidth: "fit-content" }}
          >
            Total: ${" "}
            {totalFirstCharge.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
          <IconButton
            onClick={onExportExcel}
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
              width: 48,
              height: 48,
            }}
            title="Download Excel"
          >
            <CloudDownloadIcon />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0}>
            <Table
              sx={{
                "& .MuiTableCell-root": {
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#F4F5F8" }}>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    Job Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    Lot
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    Client Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    OOST Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    Into Storage Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    First Charge
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                    Office
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">
                        {searchTerm ? "No results found" : "No data available"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row, index) => (
                    <TableRow
                      key={`${row.job_number}-${index}`}
                      hover
                      sx={{
                        "&:hover": {
                          backgroundColor: "#F9F9F9",
                        },
                      }}
                    >
                      <TableCell>{row.job_number}</TableCell>
                      <TableCell>{row.lot}</TableCell>
                      <TableCell>{row.client_name}</TableCell>
                      <TableCell>{formatDate(row.oost_date)}</TableCell>
                      <TableCell>{formatDate(row.into_storage_date)}</TableCell>
                      <TableCell>
                        {parseFloat(row.first_charge || 0).toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}
                      </TableCell>
                      <TableCell>{row.office}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        </>
      )}
    </>
  );
};

export const StorageExpectedIncomePage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [incomeData, setIncomeData] = useState([]);
  const [firstChargeData, setFirstChargeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [monthType, setMonthType] = useState("current");

  useEffect(() => {
    document.title = "Storage Income";
    fetchData();
  }, []);

  useEffect(() => {
    // Refetch first charge data when month type changes
    if (activeTab === 1) {
      fetchFirstChargeData();
    }
  }, [monthType]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data for both tabs in parallel
      const [incomeReport, firstChargeReport] = await Promise.all([
        getStorageExpectedIncome(),
        getStorageExpectedIncomeFirstCharges(false, monthType),
      ]);

      // Process data for Storage Expected Income tab
      const processedIncomeData = Array.isArray(incomeReport)
        ? incomeReport.map((item) => ({
            job_number: item.job_number || "-",
            lot: item.lot || "-",
            client_name: item.client_name || "-",
            oost_date: item.oost_date || "-",
            monthly_charge: item.monthly_charge || 0,
            office: "Roadway Moving",
          }))
        : [];

      // Process data for Storage Expected First Charge tab
      const processedFirstChargeData = Array.isArray(firstChargeReport)
        ? firstChargeReport.map((item) => ({
            job_number: item.job_number || "-",
            lot: item.lot || "-",
            client_name: item.client_name || "-",
            oost_date: item.oost_date || "-",
            into_storage_date:
              item.into_sto_date || item.into_storage_date || "-",
            first_charge: item.first_charge || 0,
            office: "Roadway Moving",
          }))
        : [];

      setIncomeData(processedIncomeData);
      setFirstChargeData(processedFirstChargeData);
    } catch (err) {
      console.error("Error fetching storage data:", err);
      setError(err.message || "Failed to fetch storage data");
      setIncomeData([]);
      setFirstChargeData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFirstChargeData = async () => {
    try {
      setLoading(true);
      setError(null);

      const firstChargeReport = await getStorageExpectedIncomeFirstCharges(
        false,
        monthType
      );

      const processedFirstChargeData = Array.isArray(firstChargeReport)
        ? firstChargeReport.map((item) => ({
            job_number: item.job_number || "-",
            lot: item.lot || "-",
            client_name: item.client_name || "-",
            oost_date: item.oost_date || "-",
            into_storage_date:
              item.into_sto_date || item.into_storage_date || "-",
            first_charge: item.first_charge || 0,
            office: "Roadway Moving",
          }))
        : [];

      setFirstChargeData(processedFirstChargeData);
    } catch (err) {
      console.error("Error fetching first charge data:", err);
      setError(err.message || "Failed to fetch first charge data");
      setFirstChargeData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    const dataToExport = activeTab === 0 ? incomeData : firstChargeData;

    if (!dataToExport || dataToExport.length === 0) {
      console.warn("No data to export");
      return;
    }

    const workbook = XLSX.utils.book_new();

    if (activeTab === 0) {
      // Storage Expected Income
      const headers = [
        "Job Number",
        "Lot",
        "Client Name",
        "OOST Date",
        "Monthly Charge",
        "Office",
      ];

      const data = dataToExport.map((row) => [
        row.job_number,
        row.lot,
        row.client_name,
        row.oost_date,
        row.monthly_charge,
        row.office,
      ]);

      const totalRow = [
        "Total",
        "",
        "",
        "",
        dataToExport.reduce(
          (sum, item) => sum + (parseFloat(item.monthly_charge) || 0),
          0
        ),
        "",
      ];

      const sheet = XLSX.utils.aoa_to_sheet([headers, ...data, totalRow]);
      XLSX.utils.book_append_sheet(workbook, sheet, "Storage Expected Income");
    } else {
      // Storage Expected First Charge
      const headers = [
        "Job Number",
        "Lot",
        "Client Name",
        "OOST Date",
        "Into Storage Date",
        "First Charge",
        "Office",
      ];

      const data = dataToExport.map((row) => [
        row.job_number,
        row.lot,
        row.client_name,
        row.oost_date,
        row.into_storage_date,
        row.first_charge,
        row.office,
      ]);

      const totalRow = [
        "Total",
        "",
        "",
        "",
        "",
        dataToExport.reduce(
          (sum, item) => sum + (parseFloat(item.first_charge) || 0),
          0
        ),
        "",
      ];

      const sheet = XLSX.utils.aoa_to_sheet([headers, ...data, totalRow]);
      XLSX.utils.book_append_sheet(
        workbook,
        sheet,
        "Storage Expected First Charge"
      );
    }

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const fileName = `Storage_${
      activeTab === 0 ? "Expected_Income" : "Expected_First_Charge"
    }_${new Date().toISOString().split("T")[0]}.xlsx`;
    saveAs(blob, fileName);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchTerm(""); // Reset search when switching tabs
    setPage(0); // Reset to first page
  };

  const tabs = [
    { label: "Storage Expected Income", component: StorageExpectedIncomeTab },
    {
      label: "Storage Expected First Charge",
      component: StorageExpectedFirstChargeTab,
    },
  ];

  const ActiveTabComponent = tabs[activeTab].component;
  const currentData = activeTab === 0 ? incomeData : firstChargeData;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F5F5F5", py: 4 }}>
      <Container maxWidth="xl">
        <Typography variant="h4" fontWeight={600} color="#2B3033" mb={3}>
          {tabs[activeTab].label}
        </Typography>

        <Card sx={{ boxShadow: "0px 2px 8px rgba(0,0,0,0.1)" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  minHeight: 48,
                },
                "& .Mui-selected": {
                  color: "#1976d2",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab key={index} label={tab.label} />
              ))}
            </Tabs>
          </Box>
          <CardContent>
            <ActiveTabComponent
              data={currentData}
              loading={loading}
              error={error}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              {...(activeTab === 1 && {
                monthType,
                setMonthType,
              })}
              onExportExcel={handleExportExcel}
            />
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};
